import { useMemo, useState } from 'react';
import { makeTranslator, type Locale } from '../lib/i18n/index.js';

export interface LoginProps {
  readonly onLogin: (email: string) => Promise<void>;
  readonly disabled?: boolean;
  readonly locale?: Locale;
}

export function Login({ onLogin, disabled, locale }: LoginProps) {
  const t = useMemo(() => makeTranslator(locale), [locale]);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onLogin(email.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} aria-label="login">
      <h1>{t('login.title')}</h1>
      <p className="cp-subtitle">{t('login.subtitle')}</p>
      <label htmlFor="cp-email-input" className="cp-mono" style={{ fontSize: 12 }}>
        {t('login.email_label')}
      </label>
      <input
        id="cp-email-input"
        className="cp-input"
        type="email"
        autoComplete="email"
        required
        placeholder={t('login.email_placeholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={disabled || submitting}
        style={{ marginTop: 4, marginBottom: 12 }}
      />
      <button
        type="submit"
        className="cp-button"
        disabled={disabled || submitting || email.length === 0}
      >
        {submitting ? t('login.submitting') : t('login.submit')}
      </button>
      {error ? <div className="cp-error">{error}</div> : null}
    </form>
  );
}
